import type { AccessType } from "@keycloak/keycloak-admin-client/lib/defs/whoAmIRepresentation";
import {
  createNamedContext,
  useEnvironment,
  useRequiredContext,
} from "@keycloak/keycloak-ui-shared";
import { PropsWithChildren, useEffect, useState } from "react";
import { useRealm } from "../../context/realm-context/RealmContext";
import { useWhoAmI } from "../../context/whoami/WhoAmI";

type AccessContextProps = {
  hasAccess: (...types: AccessType[]) => boolean;
  hasSomeAccess: (...types: AccessType[]) => boolean;
  // --- DA email tab permisssions --------------------------------------------
  isAdmin: boolean;
  // --- END DA email tab permisssions ----------------------------------------
};

export const AccessContext = createNamedContext<AccessContextProps | undefined>(
  "AccessContext",
  undefined,
);

export const useAccess = () => useRequiredContext(AccessContext);

export const AccessContextProvider = ({ children }: PropsWithChildren) => {
  const { whoAmI } = useWhoAmI();
  const { realm } = useRealm();
  const access = whoAmI.realm_access[realm] ?? [];
  // --- DA email tab permisssions --------------------------------------------
  const { keycloak } = useEnvironment();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    if (keycloak.tokenParsed)
      setIsAdmin(keycloak.tokenParsed["is-master-admin"]);
  }, [keycloak.tokenParsed]);
  // --- END DA email tab permisssions ----------------------------------------

  const hasAccess = (...types: AccessType[]): boolean => {
    return types.every(
      (type) =>
        type === "anyone" ||
        (typeof type === "function" &&
          type({ hasAll: hasAccess, hasAny: hasSomeAccess })) ||
        access.includes(type),
    );
  };

  const hasSomeAccess = (...types: AccessType[]): boolean => {
    return types.some(
      (type) =>
        type === "anyone" ||
        (typeof type === "function" &&
          type({ hasAll: hasAccess, hasAny: hasSomeAccess })) ||
        access.includes(type),
    );
  };

  return (
    <AccessContext.Provider value={{ hasAccess, hasSomeAccess, isAdmin }}>
      {children}
    </AccessContext.Provider>
  );
};
